import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';
import { useToast } from '../contexts/ToastContext';

export default function BillingPortal() {
  const navigate = useNavigate();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const { showSuccess, showError } = useToast();
  const [planId, setPlanId] = useState('');
  const [loading, setLoading] = useState(true);
  const [userBilling, setUserBilling] = useState(null);
  const [error, setError] = useState(null);
  const [editingPreferences, setEditingPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    auto_topup_enabled: false,
    auto_topup_amount_cents: 2000,
    low_balance_threshold_cents: 500,
    allow_overages: true,
    max_monthly_spend_cents: null
  });

  // Load user billing information
  useEffect(() => {
    const loadBillingInfo = async () => {
      if (!isLoaded || !isSignedIn) return;

      try {
        const userData = await apiClient.get('/api/auth/me');
        console.log('User billing data:', userData); // Debug log
        setUserBilling(userData);

        // Set preferences from user data (check both billing_preferences and wallet)
        const billingPrefs = userData.billing_preferences;
        const wallet = userData.wallet;

        if (billingPrefs || wallet) {
          setPreferences({
            auto_topup_enabled: Boolean(billingPrefs?.auto_topup_enabled ?? wallet?.auto_topup_enabled),
            auto_topup_amount_cents: billingPrefs?.auto_topup_amount_cents || wallet?.auto_topup_amount_cents || 2000,
            low_balance_threshold_cents: billingPrefs?.auto_topup_threshold_cents || wallet?.low_balance_threshold_cents || 500,
            allow_overages: Boolean(billingPrefs?.allow_overages ?? wallet?.overage_allowed),
            max_monthly_spend_cents: billingPrefs?.max_monthly_spend_cents || wallet?.spend_cap_cents
          });
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to load billing info:', error);
        setError('Failed to load billing information');
        setLoading(false);
      }
    };

    loadBillingInfo();
  }, [isLoaded, isSignedIn, apiClient]);

  const handleCheckout = async () => {
    if (!planId) {
      alert('Please select a plan.');
      return;
    }
    try {
      const { url } = await apiClient.post('/api/billing/checkout', { plan_id: planId });
      window.location.href = url;
    } catch (error) {
      console.error(error);
      alert('Error creating checkout session.');
    }
  };

  const handleFundWallet = () => {
    navigate('/plans');
  };

  const handleSavePreferences = async () => {
    try {
      console.log('Saving preferences...', preferences);
      await apiClient.put('/api/billing/preferences', preferences);

      // Reload user data to reflect changes
      const userData = await apiClient.get('/api/auth/me');
      setUserBilling(userData);
      setEditingPreferences(false);

      console.log('Showing success toast...');
      showSuccess('Billing preferences updated successfully!');
    } catch (error) {
      console.error('Failed to update preferences:', error);
      console.log('Showing error toast...');
      showError('Failed to update preferences. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    // Reset preferences to current values
    const billingPrefs = userBilling?.billing_preferences;
    const wallet = userBilling?.wallet;

    if (billingPrefs || wallet) {
      setPreferences({
        auto_topup_enabled: Boolean(billingPrefs?.auto_topup_enabled ?? wallet?.auto_topup_enabled),
        auto_topup_amount_cents: billingPrefs?.auto_topup_amount_cents || wallet?.auto_topup_amount_cents || 2000,
        low_balance_threshold_cents: billingPrefs?.auto_topup_threshold_cents || wallet?.low_balance_threshold_cents || 500,
        allow_overages: Boolean(billingPrefs?.allow_overages ?? wallet?.overage_allowed),
        max_monthly_spend_cents: billingPrefs?.max_monthly_spend_cents || wallet?.spend_cap_cents
      });
    }
    setEditingPreferences(false);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Loading billing information...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if user has billing preferences set up
  const hasBillingSetup = userBilling?.billing_preferences && userBilling.billing_preferences.plan_id;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Billing & Subscription</h1>
        <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Manage your subscription and billing preferences</p>
      </div>

      {hasBillingSetup ? (
        // Show current billing information
        <div className="space-y-6">
          {/* Current Plan */}
          <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Current Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Plan</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {userBilling.billing_preferences.plan_name || userBilling.billing_preferences.plan_id}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Fee</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  ${(userBilling.billing_preferences.monthly_fee / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Minutes Included</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {userBilling.billing_preferences.minutes_included} minutes
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Phone Numbers</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {userBilling.billing_preferences.phone_numbers_included} included
                </p>
              </div>
            </div>
          </div>

          {/* Wallet Information */}
          <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Wallet & Preferences</h2>
              <button
                onClick={() => setEditingPreferences(!editingPreferences)}
                className="px-4 py-2 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium dark:text-white"
              >
                {editingPreferences ? 'Cancel' : 'Edit Preferences'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Current Balance</p>
                <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                  ${(userBilling.wallet?.balance_cents / 100 || 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Auto-Topup</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {Boolean(userBilling.billing_preferences?.auto_topup_enabled ?? userBilling.wallet?.auto_topup_enabled) ? 'Enabled' : 'Disabled'}
                </p>
                {Boolean(userBilling.billing_preferences?.auto_topup_enabled ?? userBilling.wallet?.auto_topup_enabled) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Triggers at ${((userBilling.billing_preferences?.auto_topup_threshold_cents || userBilling.wallet?.low_balance_threshold_cents || 500) / 100).toFixed(2)}
                  </p>
                )}
              </div>
            </div>

            {editingPreferences ? (
              // Edit mode
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg dark:bg-gray-900">
                <h3 className="font-semibold text-gray-900 dark:text-white">Edit Billing Preferences</h3>

                {/* Auto-Topup Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Auto-Topup</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Automatically add funds when balance is low</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.auto_topup_enabled}
                      onChange={(e) => setPreferences({ ...preferences, auto_topup_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-teal-600 dark:bg-gray-800"></div>
                  </label>
                </div>

                {preferences.auto_topup_enabled && (
                  <>
                    {/* Trigger Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-600">
                        Trigger Amount (when to auto-topup)
                      </label>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">$</span>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={(preferences.low_balance_threshold_cents / 100).toFixed(2)}
                          onChange={(e) => setPreferences({
                            ...preferences,
                            low_balance_threshold_cents: Math.round(parseFloat(e.target.value || 0) * 100)
                          })}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white dark:border-gray-600"
                        />
                      </div>
                    </div>

                    {/* Topup Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-600">
                        Auto-Topup Amount
                      </label>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">$</span>
                        <input
                          type="number"
                          min="10"
                          step="1"
                          value={(preferences.auto_topup_amount_cents / 100).toFixed(2)}
                          onChange={(e) => setPreferences({
                            ...preferences,
                            auto_topup_amount_cents: Math.round(parseFloat(e.target.value || 10) * 100)
                          })}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white dark:border-gray-600"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Minimum $10.00</p>
                    </div>
                  </>
                )}

                {/* Allow Overages */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Allow Overages</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Allow usage beyond plan limits</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.allow_overages}
                      onChange={(e) => setPreferences({ ...preferences, allow_overages: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-teal-600 dark:bg-gray-800"></div>
                  </label>
                </div>

                {/* Monthly Spend Cap */}
                {preferences.allow_overages && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-600">
                      Monthly Spend Cap (Optional)
                    </label>
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-2">$</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={preferences.max_monthly_spend_cents ? (preferences.max_monthly_spend_cents / 100).toFixed(2) : ''}
                        onChange={(e) => setPreferences({
                          ...preferences,
                          max_monthly_spend_cents: e.target.value ? Math.round(parseFloat(e.target.value) * 100) : null
                        })}
                        placeholder="No limit"
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white dark:border-gray-600"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Leave empty for no limit</p>
                  </div>
                )}

                {/* Save/Cancel Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSavePreferences}
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-medium dark:text-white"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium dark:text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // View mode
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300 dark:text-gray-600">Auto-Topup Amount:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    ${((userBilling.billing_preferences?.auto_topup_amount_cents || userBilling.wallet?.auto_topup_amount_cents || 2000) / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300 dark:text-gray-600">Allow Overages:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {Boolean(userBilling.billing_preferences?.allow_overages ?? userBilling.wallet?.overage_allowed) ? 'Yes' : 'No'}
                  </span>
                </div>
                {(userBilling.billing_preferences?.max_monthly_spend_cents || userBilling.wallet?.spend_cap_cents) && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 dark:text-gray-300 dark:text-gray-600">Monthly Spend Cap:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${((userBilling.billing_preferences?.max_monthly_spend_cents || userBilling.wallet?.spend_cap_cents) / 100).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6">
              <button
                onClick={handleFundWallet}
                className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-medium dark:text-white"
              >
                Add Funds to Wallet
              </button>
            </div>
          </div>

          {/* Plan Features */}
          <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Plan Features</h2>
            <ul className="space-y-2">
              <li className="flex items-center text-gray-700 dark:text-gray-300 dark:text-gray-600">
                <svg className="w-5 h-5 text-teal-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {userBilling.billing_preferences.minutes_included} minutes included per month
              </li>
              <li className="flex items-center text-gray-700 dark:text-gray-300 dark:text-gray-600">
                <svg className="w-5 h-5 text-teal-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {userBilling.billing_preferences.phone_numbers_included} phone number{userBilling.billing_preferences.phone_numbers_included > 1 ? 's' : ''} included
              </li>
              <li className="flex items-center text-gray-700 dark:text-gray-300 dark:text-gray-600">
                <svg className="w-5 h-5 text-teal-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {userBilling.billing_preferences.retention_days} days recording storage
              </li>
              <li className="flex items-center text-gray-700 dark:text-gray-300 dark:text-gray-600">
                <svg className="w-5 h-5 text-teal-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Voice overage: ${(userBilling.billing_preferences.voice_overage_rate / 100).toFixed(2)} per minute
              </li>
            </ul>
          </div>
        </div>
      ) : (
        // Show plan selection for users without billing setup
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Choose a Plan</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 dark:text-gray-300 dark:text-gray-600">
            You don't have a plan set up yet. Please select a plan to get started.
          </p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="plan"
                  value="starter"
                  onChange={(e) => setPlanId(e.target.value)}
                  className="form-radio h-5 w-5 text-teal-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-teal-500 dark:bg-gray-800"
                />
                <span className="ml-3 text-gray-900 dark:text-white">Starter Plan</span>
              </label>
            </div>
            <div>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="plan"
                  value="growth"
                  onChange={(e) => setPlanId(e.target.value)}
                  className="form-radio h-5 w-5 text-teal-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-teal-500 dark:bg-gray-800"
                />
                <span className="ml-3 text-gray-900 dark:text-white">Growth Plan</span>
              </label>
            </div>
            <div>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="plan"
                  value="pro"
                  onChange={(e) => setPlanId(e.target.value)}
                  className="form-radio h-5 w-5 text-teal-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-teal-500 dark:bg-gray-800"
                />
                <span className="ml-3 text-gray-900 dark:text-white">Pro Plan</span>
              </label>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button
              onClick={handleCheckout}
              className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors font-medium dark:text-white"
            >
              Subscribe
            </button>
            <button
              onClick={() => navigate('/plans')}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium dark:text-white"
            >
              View All Plans
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
